import { UIMessage } from 'ai';
import { ChartColorToken } from 'utils/chartColors';
import type { GlobalSearchEntityType } from './globalSearch';

export interface AiChatMessageToolCallDto {
    id: number;
    toolName: string;
    toolCallId: string;
    filter: string | null;
    recordCount: number | null;
    /** Whether the record limit cut the result; null for a call stored before this was kept. */
    partialResults: boolean | null;
    totalCount: number | null;
    counts: AiToolOutputCounts | null;
    inputBytes: number | null;
    outputBytes: number | null;
    status: string;
    inputPayload: Record<string, unknown> | null;
    outputPayload: Record<string, unknown> | null;
    errorText: string | null;
    createdAt: string;
}

/**
 * Where Crespect AI stops being a page inside the portal and takes the screen over instead. The
 * same 768px line as `--breakpoint-sm`, read through `matchMedia` — the convention
 * `GLOBAL_SEARCH_NARROW_QUERY` already set.
 */
export const AI_CHAT_MOBILE_QUERY = '(max-width: 767.98px)';

/** What the AI wrote about a chat after its answers, and the ready-made prompt that started it. */
interface AiChatThreadSummaryFields {
    /** One line on what the chat is about; null until the AI has written one. */
    description: string | null;
    /** The chat's kind as the AI classified it (`general` when it fits no other); null until then. */
    kind: string | null;
    /** The id of the ready-made prompt the chat started from; null for a chat the user typed. */
    promptId: string | null;
}

export interface AiChatThreadSummary extends AiChatThreadSummaryFields {
    id: number;
    title: string;
    lastMessageAt: string;
}

export interface AiChatThreadMessageDto {
    id: number;
    role: string;
    content: string;
    createdAt: string;
    toolCalls?: AiChatMessageToolCallDto[];
}

export interface AiChatThreadDetail extends AiChatThreadSummaryFields {
    id: number;
    title: string;
    messages: AiChatThreadMessageDto[];
}

export interface ToolCallDebugInfo {
    toolName?: string;
    status?: string;
    filter?: string | null;
    inputBytes?: number | null;
    outputBytes?: number | null;
    recordCount?: number | null;
    requestedFields?: string[] | null;
    errorText?: string | null;
}

interface PortalChatMessageMetadata {
    createdAt?: string;
    aiChatMessageId?: number;
    toolDebug?: Record<string, ToolCallDebugInfo>;
    /**
     * The turn's two stored times, the question's and the answer's: live from the stream's start and finish, from
     * history the two messages' `createdAt`. So the turn's length reads the same after a reload.
     */
    turnStartedAt?: string;
    turnFinishedAt?: string;
    /** From history, each tool call's summary by its id; live, the summary is the tool part's output. */
    toolSummaries?: Record<string, AiToolOutputSummary>;
}

interface ThreadDataPart {
    threadId: string;
    isNew: boolean;
    title?: string;
    userMessageId?: number;
}

/** The chat's title and summary as the backend stored them after an answer; every key is sent, unset ones null. */
export interface TitleDataPart extends AiChatThreadSummaryFields {
    threadId: string | number;
    title: string | null;
}

interface PortalChatDataParts {
    [key: string]: unknown;
    thread: ThreadDataPart;
    title: TitleDataPart;
}

export type PortalChatMessage = UIMessage<PortalChatMessageMetadata, PortalChatDataParts>;

export interface ChartSeries {
    label: string;
    data: number[];
    color?: ChartColorToken;
}

interface ChartPieDatum {
    label: string;
    value: number;
}

export interface ChartSpec {
    type: 'bar' | 'line' | 'pie';
    title?: string;
    description?: string;
    x?: string[];
    series?: ChartSeries[];
    data?: ChartPieDatum[];
    /** Bar only: draw the series as one stacked column per x value instead of grouped bars. */
    stacked?: boolean;
    /** Anchor the value axis at zero instead of the data's own extent. */
    zeroBaseline?: boolean;
}

export interface ChooseOption {
    /** What the reader sees, and the whole of the next user message when the choice carries no hint. */
    label: string;
    /**
     * The detail that tells two same-named candidates apart ("Tallinn office"). Shown beside the label
     * and sent with it as `label (hint)`, because the label alone would not say which record was picked.
     */
    hint?: string;
    /** A search hit's record type: the option's tile then shows that type's glyph in its family's colours. */
    entityType?: GlobalSearchEntityType;
}

export interface ChooseOptionsQuestion {
    /**
     * What the question settles ("Period"), which each answer carries when several questions are sent
     * together. Absent only on a question stored before questions came in blocks, which was always alone.
     */
    topic?: string;
    question: string;
    options: ChooseOption[];
}

export interface ChooseOptionsSpec {
    questions: ChooseOptionsQuestion[];
}

export type AiToolCallStatus = 'pending' | 'success' | 'error';

/** A meeting brief's counts, read from its output. */
export interface AiToolOutputCounts {
    cases?: number;
    bdEfforts?: number;
}

/**
 * What the backend sends for a tool whose output stays on the server: the rows it returned (null for a tool without
 * a counted list), whether the record limit cut them, and how many matched where the tool reports it.
 */
export interface AiToolOutputSummary {
    count: number | null;
    partial: boolean;
    total: number | null;
    counts?: AiToolOutputCounts;
}

export interface AiToolCallHistoryItem {
    id: string;
    name: string;
    state: string;
    status: AiToolCallStatus;
    input?: unknown;
    output?: unknown;
    errorText?: string;
    /** Absent for a tool whose output the chat renders (a Figure, a choice), which carries no summary. */
    summary?: AiToolOutputSummary;
}
